import { Vue, Component, Prop, Inject, InjectReactive, Watch } from 'vue-property-decorator';
import {
  Step, StepPager, Btn, Toolbar, Field, DropdownField, IStepComponent, GetService, IMapping, IMappingDetail,
  IField, ClearAfterFunction, IFileSource, VMenu, MenuItem
} from '@samc/centricity-core';
import { AjaxHelper, errToMessage } from '@samc/common';
import { ScratchpadExtRefService, IScratchpadExtRefCompareGivenFieldsOptions } from '@samc/query-builder-client';
import { VuePlugin } from 'vuera';
import { ScratchCompare } from '@samc/querybuilder/core/ClientApp/modular/Scratchpad/CompareTool/ScratchCompare';
import { download, gotoPost } from '../../utils';
import { IPackageItem } from '@/Interfaces';
import { SurviveName, SurviveOption } from '@/Types';
import '@/registerVuePlugins';
import { ConfigurationContextProps, QueryProps } from '@samc/querybuilder';
Vue.use(VuePlugin);

interface ILsField extends IField {
  lsFieldName: string;
}

@Component({
  name: 'alternate-compare-step',
  components: {
    Step, StepPager, Btn, Toolbar, Field, DropdownField, VMenu, MenuItem, Compare: (ScratchCompare as any)
  }
})
export default class AlternateCompareComponent extends Vue implements IStepComponent {
  @Prop() public verificationToken!: string;
  @Prop({ default: '' }) public packageId!: string | null;
  @Prop() public scratchPadUrl!: string;
  @Prop({ default: -1 }) public loanListId!: number;
  @Prop({ default: 'loan' }) public domainName!: string;
  @Prop({ required: true }) public alternate!: string;
  @Prop({ default: () => [] }) public packages!: IPackageItem[];
  @Prop({ default: 'Compare' }) public title!: string;
  @Prop({ default: '/api/' }) public apiRoot?: string;
  @Prop({ default: 'LoanId' }) public compareKey!: string;
  @Prop({ default: 'loan' }) public mergeType!: 'loan' | 'cashflow';
  @Prop() public lsUrl!: string;
  @Prop() public hiearchy!: SurviveName[];
  @Prop() public goldenIsNullOption: SurviveOption = 'retain';
  @Prop() public goldenIsNotNullOption: SurviveOption = 'retain';

  @Inject('getService') public getService!: GetService;

  @InjectReactive() public readonly mapping!: IMapping<IMappingDetail> | null;
  @InjectReactive() public readonly fileSource!: IFileSource | null;
  @Inject('clearAfter') public clearAfter!: ClearAfterFunction;

  public id = 'alternate-compare';
  public isValid: boolean = true;
  public isDirty: boolean = false;
  public isReadonly: boolean = false;
  public unmappedFieldId = 0;
  public fields: string[] = [];
  public innerPackageId: string = '';
  public description: string = '';
  public optional: boolean = false;
  public showMenu: boolean = false;

  public get lsProps(): QueryProps & ConfigurationContextProps {
    return {
      antiforgeryToken: this.verificationToken,
    }
  }

  /**
   * The wrapper for the component instance
   */
  public get component(): IStepComponent | null {
    return {
      isValid: this.isValid,
      isDirty: this.isDirty,
      isReadonly: this.isReadonly,
      clear: this.clear,
      initializeEvents: this.initializeEvents,
      clearEvents: this.clearEvents,
      initialize: this.initialize,
      enter: this.enter,
      skip: this.skip,
      leave: this.leave
    };
  };

  public get postData(): any {
    const pkg = this.packages.find(x => x.id === this.innerPackageId);
    var comparisonFlowVal = 0;
    if (this.alternate?.toString().toLowerCase() == 'duedil') {
      comparisonFlowVal = 4;
    }
    else if (this.alternate?.toString().toLowerCase() == 'seller') {
      comparisonFlowVal = 3;
    }
    else if (this.alternate?.toString().toLowerCase() == 'servicer') {
      comparisonFlowVal = 6;
    }


    const extraFields = this.mergeType === 'cashflow' ? [this.compareKey] : [];

    return {
      survivorshipMergeType: this.mergeType,
      left: {
        key: this.compareKey,
        execGivenFieldsSpec: {
          domainName: this.domainName,
          externalSetIds: [(pkg?.loanListId ?? this.loanListId).toString()],
          fields: [...this.fields, ...extraFields]
        }
      },
      right: {
        key: this.compareKey,
        execGivenFieldsSpec: {
          domainName: this.domainName,
          externalSetIds: [(pkg?.loanListId ?? this.loanListId).toString()],
          dataAlternateCode: this.alternate,
          fields: [...this.fields, ...extraFields]
        }
      },
      comparisonFlow: comparisonFlowVal
    };
  }

  public async mounted() {
    this.verificationToken ??= await this.$verificationToken();
  }
  public async clear(): Promise<void> {
    this.isValid = true;
    this.isDirty = false;
    this.isReadonly = false;
  }

  public async initialize(): Promise<boolean> {
    if (this.packages.length == 1) {
      this.innerPackageId = this.packages[0].id;
    }

    var fieldService = this.getService('field');
    if (fieldService !== null && this.mapping !== null) {
      const fieldIds = this.mapping.details
        .filter(x => typeof x.internalFieldId === 'number' && x.internalFieldId > this.unmappedFieldId)
        .map(x => x.internalFieldId as number);
      try {
        this.$loading('Loading Fields');
        const result = await fieldService.getFields<ILsField>({ fieldIds });

        this.fields = result.items.filter(f => f.lsFieldName != null).map(x => x.lsFieldName);
      } catch (err: any) {
        this.$log('Fields failed to load', err);
        this.$alert({
          type: 'error',
          title: 'Fields failed to load',
          errors: errToMessage(err),
          closingOption: 'holdOpen'
        });
        return false;
      } finally {
        this.$loading();
      }
    }
    return true;
  }

  public async enter(): Promise<void> {
    await this.clearAfter(this.id);
  }

  public async skip(): Promise<void> {
    // Nothing todo here yet
  }

  /**
   * Intialize events for the event bus
   **/
  public async initializeEvents(): Promise<void> {
    // Do Nothing
  }

  /**
   * Remove events from the event bus
   **/
  public async clearEvents(): Promise<void> {
    // Do Nothing
  }

  public async leave(dir: 1 | -1): Promise<boolean> {
    return true;
  }

  public async gotoSurvivorship(useTargeted: boolean = false) {
    if (await this.$confirm.warning('Leaving the page', 'Are you sure?')) {
      const data = {
        ...(this.innerPackageId.length === 0 ? { loanListId: this.loanListId } : { packageId: this.innerPackageId }),
        ...(useTargeted ? { sourceId: this.fileSource?.id } : {}),
        hiearchy: this.hiearchy,
        goldenIsNullOption: useTargeted ? this.goldenIsNullOption : 'retain',
        goldenIsNotNullOption: useTargeted ? this.goldenIsNotNullOption : 'retain'
      };
      gotoPost('/DataImport/Survivorship', this.verificationToken, data);
    }
  }

  /**
   * Callback method to get data from an end point for the compare tool
   **/
  public async compareEndpoint(options: IScratchpadExtRefCompareGivenFieldsOptions) {
    const service = this.getScratchpadService();

    try {
      
      return await service.compareGivenFields({ ...this.postData, options: { ...options, survivorshipMergeType: this.mergeType } as any });
    } catch (ex: any) {
      if (ex.isAxiosError) {
        const data = ex.response.data;
        if (data?.DeveloperMessage?.message.indexOf('404') > -1) {
          this.$alert.error('Raw data for file could not be found!');
        } else {
          this.$alert.error('A scratchpad error ocurred!');
        }
      } else {
        this.$alert.error('An error occurred!');
      }
    }
  }

  /**
   * Callback method to be used when exporting the compared data
   **/
  public async onExport(options: any) {
    const service = this.getScratchpadService();

    try {
      const data = await service.compareGivenFieldsAndExport({ ...this.postData, options: { ...options, survivorshipMergeType: this.mergeType } as any });
      download(data, 'Compare.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch (ex: any) {
      if (ex.isAxiosError) {
        const data = ex.response.data;
        if (data?.DeveloperMessage?.message.indexOf('404') > -1) {
          this.$alert.error('Raw data for file could not be found!');
        } else {
          this.$alert.error('A scratchpad error ocurred!');
        }
      } else {
        this.$alert.error('An error occurred!');
      }
    }
  }

  /**
   * Will parse the scratchpad url given and add a / if one does not exist on the end
   */
  public get parsedLsUrl(): string {
    const url = this.lsUrl ?? '';
    if (url.endsWith('/')) return url + 'api/';
    return url + '/api/';
  }

  @Watch('packageId', { immediate: true })
  public onPackageIdUpdate(newPackageId: string | null) {
    this.innerPackageId = newPackageId ?? '';
  }

  private getScratchpadService(): ScratchpadExtRefService {
    return new ScratchpadExtRefService(new AjaxHelper(this.lsUrl, this.verificationToken, null, this.$token));
  }
}